import { anonId } from '@/lib/anonId'
import { leseSse } from '@/lib/sse'

// ── Health ──────────────────────────────────────────────────────────────
export interface Health {
  status: 'ok' | 'degraded'
  db: 'ok' | 'error'
  redis: 'ok' | 'error'
  env: string
  version: string
  anthropic: 'configured' | 'unconfigured'
}

/**
 * Holt den Systemstatus.
 *
 * 503 ist hier eine gültige, informative Antwort — das Backend meldet damit
 * „eine Komponente ist unten", und genau das wollen wir anzeigen. Nur echte
 * Transportfehler oder unerwartete Codes werfen.
 */
export async function fetchHealth(signal?: AbortSignal): Promise<Health> {
  const response = await fetch('/health', {
    signal,
    headers: { accept: 'application/json' },
  })

  if (!response.ok && response.status !== 503) {
    throw new Error(`Health antwortete mit ${String(response.status)}`)
  }

  return (await response.json()) as Health
}

// ── Dialekte ────────────────────────────────────────────────────────────
export interface DialektTheme {
  primary: string
  on_primary: string
  surface: string
  surface_container: string
  on_surface: string
  on_surface_muted: string
  accent: string
  outline: string
  display_font: 'serif' | 'sans' | 'slab'
  motif: string
}

export interface Mikrocopy {
  cta: string
  leer: string
  laedt: string
}

export interface Dialekt {
  id: string
  name: string
  region: string
  tagline: string
  status: 'stable' | 'beta'
  theme: DialektTheme
  mikrocopy: Mikrocopy
}

export async function fetchDialekte(signal?: AbortSignal): Promise<Dialekt[]> {
  const antwort = await fetch('/api/dialects', { signal, headers: { accept: 'application/json' } })
  if (!antwort.ok) throw new Error(`Dialekte antworteten mit ${String(antwort.status)}`)
  const nutzlast = (await antwort.json()) as { dialekte: Dialekt[] }
  return nutzlast.dialekte
}

// ── Übersetzen ──────────────────────────────────────────────────────────
export interface GlossarEintrag {
  wort: string
  hoch: string
  hinweis?: string
}

export type Ereignis =
  | { typ: 'start'; dialekt_id: string; modell: string; quelle: 'live' | 'cache'; grund: string }
  | { typ: 'text'; text: string }
  | {
      typ: 'fertig'
      id: string
      dialekt_text: string
      tts_text: string
      glossar: GlossarEintrag[]
      konfidenz: number
      intensitaet_effektiv: number
      anmerkung: string | null
      modell: string
      quelle: 'live' | 'cache'
      kosten_mikrocent: number
      latenz_ms: number
      kontingent_rest: number
    }
  | { typ: 'fehler'; code: string; meldung: string; retry_after?: number; kontingent_rest?: number }

/** Überträgt einen Text in genau einen Dialekt. */
export async function* uebersetze(
  text: string,
  dialekt: string,
  signal?: AbortSignal,
): AsyncGenerator<Ereignis> {
  const antwort = await fetch('/api/translate', {
    method: 'POST',
    signal,
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({ text, dialekt, anon_id: anonId() }),
  })

  if (!antwort.ok) {
    // Validierungsfehler kommen als normale HTTP-Antwort, nicht im Strom.
    yield {
      typ: 'fehler',
      code: antwort.status === 422 ? 'invalid_input' : 'internal',
      meldung:
        antwort.status === 422
          ? 'Damit kann die Übersetzung nichts anfangen.'
          : 'Da ist etwas schiefgegangen. Probier es nochmal.',
    }
    return
  }

  for await (const { name, daten } of leseSse(antwort)) {
    if (name === 'start' || name === 'text' || name === 'fertig' || name === 'fehler') {
      yield { typ: name, ...(daten as object) } as Ereignis
    }
  }
}

// ── Kontingent ──────────────────────────────────────────────────────────
export interface KontingentInfo {
  rest: number
  grenze: number
}

export async function fetchKontingent(signal?: AbortSignal): Promise<KontingentInfo> {
  const antwort = await fetch('/api/kontingent', { signal, headers: { accept: 'application/json' } })
  if (!antwort.ok) throw new Error(`Kontingent antwortete mit ${String(antwort.status)}`)
  return (await antwort.json()) as KontingentInfo
}
